import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Mono:ital,wght@0,400;0,500;1,400&family=DM+Sans:wght@300;400;500&display=swap');`;

const styles = `
  ${FONTS}
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ink: #0d0d0f;
    --paper: #f5f2ec;
    --accent: #c8430a;
    --accent2: #2a6b4f;
    --muted: #9e9a91;
    --border: #dbd6cd;
    --card: #ffffff;
    --warn: #c8430a;
    --good: #2a6b4f;
    --mid: #b07c12;
  }

  body { background: var(--paper); font-family: 'DM Sans', sans-serif; color: var(--ink); }

  .app {
    min-height: 100vh;
    display: grid;
    grid-template-rows: auto 1fr;
  }

  .header {
    background: var(--ink);
    padding: 20px 40px;
    display: flex;
    align-items: center;
    gap: 16px;
    border-bottom: 3px solid var(--accent);
  }

  .header-nav-btn {
    background: none;
    border: none;
    color: var(--muted);
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 3px;
    transition: all 0.2s;
    text-decoration: none;
  }

  .header-nav-btn:hover {
    color: white;
    background: rgba(255, 255, 255, 0.1);
  }

  .header-mark {
    width: 36px; height: 36px;
    background: var(--accent);
    display: grid; place-items: center;
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 18px;
    color: white;
    transform: rotate(-3deg);
  }
  .header-title {
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 20px;
    color: white;
    letter-spacing: -0.02em;
  }
  .header-sub {
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    color: var(--muted);
    margin-left: auto;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .main {
    display: grid;
    grid-template-columns: 400px 1fr;
    height: calc(100vh - 67px);
  }

  .left {
    background: var(--card);
    border-right: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    overflow-y: auto;
  }

  .panel-section {
    padding: 28px 28px 0;
    border-bottom: 1px solid var(--border);
    padding-bottom: 28px;
  }
  .panel-label {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--muted);
    margin-bottom: 14px;
  }

  .dropzone {
    border: 2px dashed var(--border);
    border-radius: 4px;
    padding: 36px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
    position: relative;
    background: var(--paper);
  }
  .dropzone:hover, .dropzone.drag-over {
    border-color: var(--accent);
    background: #fdf5f0;
  }
  .dropzone input { position: absolute; inset: 0; opacity: 0; cursor: pointer; width: 100%; }
  .drop-icon { font-size: 32px; margin-bottom: 10px; display: block; }
  .drop-title {
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 15px;
    margin-bottom: 4px;
  }
  .drop-hint { font-size: 12px; color: var(--muted); }
  .file-types {
    display: flex;
    gap: 6px;
    justify-content: center;
    margin-top: 12px;
  }
  .file-badge {
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    background: var(--ink);
    color: white;
    padding: 2px 7px;
    border-radius: 2px;
  }

  .file-preview {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #f0fdf5;
    border: 1px solid #bbf7d0;
    border-radius: 4px;
    padding: 12px 14px;
    margin-top: 12px;
  }
  .file-icon { font-size: 22px; }
  .file-name {
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    font-weight: 500;
    word-break: break-all;
  }
  .file-size { font-size: 11px; color: var(--muted); }
  .file-remove {
    margin-left: auto;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 16px;
    color: var(--muted);
    padding: 2px;
  }
  .file-remove:hover { color: var(--warn); }

  .rubric-input {
    width: 100%;
    min-height: 100px;
    border: 1px solid var(--border);
    border-radius: 4px;
    padding: 12px;
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    resize: vertical;
    background: var(--paper);
    color: var(--ink);
    outline: none;
    transition: border-color 0.2s;
    line-height: 1.6;
  }
  .rubric-input:focus { border-color: var(--accent); }

  .subject-input {
    width: 100%;
    border: 1px solid var(--border);
    border-radius: 4px;
    padding: 10px 12px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    background: var(--paper);
    color: var(--ink);
    outline: none;
    transition: border-color 0.2s;
  }
  .subject-input:focus { border-color: var(--accent); }

  .eval-btn {
    margin: 20px 28px;
    background: var(--accent);
    color: white;
    border: none;
    border-radius: 4px;
    padding: 14px 20px;
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 15px;
    cursor: pointer;
    width: calc(100% - 56px);
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    letter-spacing: -0.01em;
  }
  .eval-btn:hover:not(:disabled) { background: #a53508; transform: translateY(-1px); }
  .eval-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  .right {
    overflow-y: auto;
    padding: 40px;
    background: var(--paper);
  }

  .empty-state {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: var(--muted);
    gap: 12px;
  }
  .empty-icon { font-size: 56px; opacity: 0.3; }
  .empty-title {
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 22px;
    color: var(--ink);
    opacity: 0.3;
  }
  .empty-sub { font-size: 14px; }

  .loading-state {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 20px;
  }
  .loading-ring {
    width: 56px; height: 56px;
    border: 3px solid var(--border);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin 0.9s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  .loading-label {
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    color: var(--muted);
    animation: pulse 1.5s ease-in-out infinite;
  }
  @keyframes pulse { 0%,100%{opacity:0.4} 50%{opacity:1} }

  .result-header {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    margin-bottom: 32px;
    padding-bottom: 24px;
    border-bottom: 2px solid var(--border);
  }
  .score-circle {
    width: 90px; height: 90px;
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border: 3px solid;
  }
  .score-num {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 28px;
    line-height: 1;
  }
  .score-denom {
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    color: var(--muted);
  }
  .score-high { border-color: var(--good); color: var(--good); }
  .score-mid { border-color: var(--mid); color: var(--mid); }
  .score-low { border-color: var(--warn); color: var(--warn); }

  .result-meta { flex: 1; }
  .result-title {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 26px;
    letter-spacing: -0.03em;
    margin-bottom: 6px;
  }
  .result-verdict { font-size: 15px; color: var(--muted); line-height: 1.5; }
  .grade-badge {
    display: inline-block;
    font-family: 'DM Mono', monospace;
    font-weight: 500;
    font-size: 13px;
    padding: 3px 10px;
    border-radius: 3px;
    margin-top: 8px;
  }

  .section {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 6px;
    margin-bottom: 20px;
    overflow: hidden;
  }
  .section-head {
    padding: 16px 20px;
    background: var(--ink);
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .section-head-title {
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 14px;
    color: white;
    letter-spacing: -0.01em;
  }
  .section-head-icon { font-size: 16px; }
  .section-body { padding: 20px; }

  .rubric-table { width: 100%; border-collapse: collapse; }
  .rubric-table th {
    text-align: left;
    font-family: 'DM Mono', monospace;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--muted);
    padding: 0 12px 10px 0;
    border-bottom: 1px solid var(--border);
  }
  .rubric-table td {
    padding: 10px 12px 10px 0;
    border-bottom: 1px solid #f0ece5;
    font-size: 13px;
    vertical-align: top;
  }
  .rubric-table tr:last-child td { border-bottom: none; }
  .criterion-name { font-weight: 500; }
  .criterion-score {
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    white-space: nowrap;
  }
  .criterion-note { color: #555; line-height: 1.5; }

  .score-bar-wrap {
    height: 5px;
    background: var(--border);
    border-radius: 3px;
    margin-top: 4px;
    width: 80px;
  }
  .score-bar { height: 100%; border-radius: 3px; }

  .feedback-list { list-style: none; display: flex; flex-direction: column; gap: 10px; }
  .feedback-item {
    display: flex;
    gap: 10px;
    font-size: 13px;
    line-height: 1.6;
    padding: 10px 14px;
    border-radius: 4px;
  }
  .feedback-item.strength { background: #f0fdf5; border-left: 3px solid var(--good); }
  .feedback-item.weakness { background: #fff8f0; border-left: 3px solid var(--warn); }
  .feedback-item.suggestion { background: #f0f8ff; border-left: 3px solid #3b82f6; }
  .feedback-dot { font-size: 15px; flex-shrink: 0; margin-top: 1px; }

  .overall-text { font-size: 14px; line-height: 1.7; color: #333; }

  .reset-btn {
    background: none;
    border: 1px solid var(--border);
    border-radius: 4px;
    padding: 8px 16px;
    font-family: 'DM Mono', monospace;
    font-size: 12px;
    cursor: pointer;
    color: var(--muted);
    transition: all 0.2s;
    margin-left: auto;
    display: block;
    margin-bottom: 24px;
  }
  .reset-btn:hover { border-color: var(--accent); color: var(--accent); }

  .error-box {
    background: #fff5f5;
    border: 1px solid #fecaca;
    border-radius: 4px;
    padding: 16px 20px;
    font-size: 13px;
    color: #b91c1c;
    display: flex;
    gap: 10px;
    align-items: flex-start;
    margin-bottom: 20px;
  }

  .api-key-note {
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 4px;
    padding: 12px 16px;
    font-size: 12px;
    color: #92400e;
    font-family: 'DM Mono', monospace;
    margin: 16px 28px 0;
    line-height: 1.6;
  }
`;

function getFileIcon(name) {
  const ext = name.split(".").pop()?.toLowerCase();
  if (ext === "pdf") return "📄";
  if (["doc", "docx"].includes(ext)) return "📝";
  if (["txt", "md"].includes(ext)) return "📃";
  return "📁";
}

function formatBytes(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function getScoreClass(score, max) {
  const pct = score / max;
  if (pct >= 0.75) return "score-high";
  if (pct >= 0.5) return "score-mid";
  return "score-low";
}

function getBarColor(pct) {
  if (pct >= 0.75) return "var(--good)";
  if (pct >= 0.5) return "var(--mid)";
  return "var(--warn)";
}

function getGradeStyle(grade) {
  const g = grade?.toUpperCase();
  if (["A+", "A", "A-"].some(x => g?.startsWith(x))) return { background: "#dcfce7", color: "#15803d" };
  if (["B+", "B", "B-"].some(x => g?.startsWith(x))) return { background: "#dbeafe", color: "#1d4ed8" };
  if (["C+", "C", "C-"].some(x => g?.startsWith(x))) return { background: "#fef9c3", color: "#a16207" };
  return { background: "#fee2e2", color: "#b91c1c" };
}

async function readFileAsBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(",")[1]);
    r.onerror = () => rej(new Error("File read failed"));
    r.readAsDataURL(file);
  });
}

async function readFileAsText(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(new Error("File read failed"));
    r.readAsText(file);
  });
}

async function evaluateWithClaude(file, rubric, subject, apiKey) {
  const ext = file.name.split(".").pop()?.toLowerCase();
  const isPDF = ext === "pdf";
  const isText = ["txt", "md"].includes(ext);

  let userContent = [];

  if (isPDF) {
    const b64 = await readFileAsBase64(file);
    userContent.push({ type: "document", source: { type: "base64", media_type: "application/pdf", data: b64 } });
  } else if (isText) {
    const text = await readFileAsText(file);
    userContent.push({ type: "text", text: `Assignment Content:\n\n${text}` });
  } else {
    userContent.push({ type: "text", text: `[DOCX file uploaded: "${file.name}". Evaluate based on filename and any rubric provided.]` });
  }

  const rubricSection = rubric?.trim() ? `\nRUBRIC:\n${rubric}\n` : "\nNo rubric provided — use standard academic criteria.\n";
  const subjectSection = subject?.trim() ? `Subject: ${subject}\n` : "";

  userContent.push({
    type: "text",
    text: `${subjectSection}${rubricSection}
Evaluate this assignment. Respond ONLY with valid JSON (no markdown, no backticks):

{
  "overallScore": <0-100>,
  "maxScore": 100,
  "grade": "<A+/A/A-/B+/B/B-/C+/C/C-/D/F>",
  "verdict": "<one sentence verdict>",
  "overallFeedback": "<2-3 sentences qualitative feedback>",
  "criteria": [{"name":"","score":0,"maxScore":0,"comment":""}],
  "strengths": [""],
  "weaknesses": [""],
  "suggestions": [""]
}

Use 4-6 criteria. Be specific and academically rigorous.`
  });

  const headers = { "Content-Type": "application/json" };
  if (apiKey) headers["x-api-key"] = apiKey;

  const response = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers,
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: 1000,
      messages: [{ role: "user", content: userContent }],
    }),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.error?.message || `API error ${response.status}`);
  }

  const data = await response.json();
  const raw = data.content?.map(b => b.text || "").join("").trim();
  return JSON.parse(raw.replace(/```json|```/g, "").trim());
}

function ScoreBar({ score, max }) {
  const pct = Math.min(score / max, 1);
  return (
    <div className="score-bar-wrap">
      <div className="score-bar" style={{ width: `${pct * 100}%`, background: getBarColor(pct) }} />
    </div>
  );
}

function Results({ result, onReset }) {
  const scoreClass = getScoreClass(result.overallScore, result.maxScore);
  const gradeStyle = getGradeStyle(result.grade);
  return (
    <div>
      <button className="reset-btn" onClick={onReset}>↩ New Evaluation</button>
      <div className="result-header">
        <div className={`score-circle ${scoreClass}`}>
          <span className="score-num">{result.overallScore}</span>
          <span className="score-denom">/ {result.maxScore}</span>
        </div>
        <div className="result-meta">
          <div className="result-title">Evaluation Complete</div>
          <div className="result-verdict">{result.verdict}</div>
          <span className="grade-badge" style={gradeStyle}>Grade: {result.grade}</span>
        </div>
      </div>

      <div className="section">
        <div className="section-head"><span className="section-head-icon">📋</span><span className="section-head-title">Overall Feedback</span></div>
        <div className="section-body"><p className="overall-text">{result.overallFeedback}</p></div>
      </div>

      <div className="section">
        <div className="section-head"><span className="section-head-icon">📊</span><span className="section-head-title">Rubric Breakdown</span></div>
        <div className="section-body">
          <table className="rubric-table">
            <thead><tr><th>Criterion</th><th>Score</th><th>Comments</th></tr></thead>
            <tbody>
              {result.criteria?.map((c, i) => (
                <tr key={i}>
                  <td><span className="criterion-name">{c.name}</span><ScoreBar score={c.score} max={c.maxScore} /></td>
                  <td><span className="criterion-score">{c.score}/{c.maxScore}</span></td>
                  <td className="criterion-note">{c.comment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="section">
        <div className="section-head"><span className="section-head-icon">⚡</span><span className="section-head-title">Strengths & Weaknesses</span></div>
        <div className="section-body">
          <ul className="feedback-list">
            {result.strengths?.map((s, i) => <li key={`s${i}`} className="feedback-item strength"><span className="feedback-dot">✅</span><span>{s}</span></li>)}
            {result.weaknesses?.map((w, i) => <li key={`w${i}`} className="feedback-item weakness"><span className="feedback-dot">⚠️</span><span>{w}</span></li>)}
          </ul>
        </div>
      </div>

      {result.suggestions?.length > 0 && (
        <div className="section">
          <div className="section-head"><span className="section-head-icon">💡</span><span className="section-head-title">Improvement Suggestions</span></div>
          <div className="section-body">
            <ul className="feedback-list">
              {result.suggestions.map((s, i) => <li key={i} className="feedback-item suggestion"><span className="feedback-dot">→</span><span>{s}</span></li>)}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AssignmentEvaluator() {
  const [file, setFile] = useState(null);
  const [rubric, setRubric] = useState("");
  const [subject, setSubject] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef();
  const navigate = useNavigate();

  const handleFile = useCallback((f) => {
    if (!f) return;
    const allowed = ["pdf", "doc", "docx", "txt", "md"];
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (!allowed.includes(ext)) { setError("Unsupported file type. Use PDF, DOCX, or TXT."); return; }
    setError(null); setResult(null); setFile(f);
  }, []);

  const onDrop = (e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); };

  const onEvaluate = async () => {
    if (!file) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const res = await evaluateWithClaude(file, rubric, subject, apiKey);
      setResult(res);
    } catch (e) {
      setError(e.message || "Evaluation failed. Check your API key and try again.");
    } finally { setLoading(false); }
  };

  const onReset = () => { setFile(null); setResult(null); setError(null); setRubric(""); setSubject(""); };

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        <header className="header">
          <button className="header-nav-btn" onClick={() => navigate("/")}>&larr; Back to Tools</button>
          <div className="header-mark">AE</div>
          <div className="header-title">Assignment Evaluator</div>
          <div className="header-sub">Powered by Fly Code Innovation</div>
        </header>
        <div className="main">
          <aside className="left">
            <div className="panel-section">
              <div className="panel-label">01 — Upload Assignment</div>
              <div
                className={`dropzone${drag ? " drag-over" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
              >
                <input ref={inputRef} type="file" accept=".pdf,.doc,.docx,.txt,.md"
                  onChange={(e) => handleFile(e.target.files[0])} onClick={(e) => e.stopPropagation()} />
                <span className="drop-icon">⬆</span>
                <div className="drop-title">Drop file here</div>
                <div className="drop-hint">or click to browse</div>
                <div className="file-types">
                  {["PDF","DOCX","TXT","MD"].map(t => <span key={t} className="file-badge">{t}</span>)}
                </div>
              </div>
              {file && (
                <div className="file-preview">
                  <span className="file-icon">{getFileIcon(file.name)}</span>
                  <div>
                    <div className="file-name">{file.name}</div>
                    <div className="file-size">{formatBytes(file.size)}</div>
                  </div>
                  <button className="file-remove" onClick={() => { setFile(null); setResult(null); }}>✕</button>
                </div>
              )}
            </div>

            <div className="panel-section">
              <div className="panel-label">02 — Subject / Course (optional)</div>
              <input className="subject-input" placeholder="e.g. MIT 8105 — Network Architecture"
                value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>

            <div className="panel-section">
              <div className="panel-label">03 — Rubric / Marking Criteria (optional)</div>
              <textarea className="rubric-input"
                placeholder={"Paste your rubric here, e.g.:\n- Content & Accuracy: 30%\n- Structure: 20%\n- Analysis: 30%\n- Referencing: 20%"}
                value={rubric} onChange={(e) => setRubric(e.target.value)} />
            </div>

            <div className="panel-section">
              <div className="panel-label">04 — Anthropic API Key</div>
              <input className="subject-input" type="password" placeholder="sk-ant-..."
                value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
            </div>

            <div className="api-key-note">
              ℹ️ Get your API key at console.anthropic.com — it is never stored and only sent directly to Anthropic's API.
            </div>

            <button className="eval-btn" disabled={!file || loading || !apiKey} onClick={onEvaluate}>
              {loading ? "⏳ Evaluating..." : "▶ Evaluate Assignment"}
            </button>
          </aside>

          <main className="right">
            {error && <div className="error-box"><span>⚠️</span><span>{error}</span></div>}
            {loading && (
              <div className="loading-state">
                <div className="loading-ring" />
                <div className="loading-label">Analysing assignment with Claude AI…</div>
              </div>
            )}
            {!loading && !result && !error && (
              <div className="empty-state">
                <span className="empty-icon">🎓</span>
                <div className="empty-title">Ready to Evaluate</div>
                <p className="empty-sub">Upload an assignment, add your API key,<br />optionally paste a rubric, then hit Evaluate.</p>
              </div>
            )}
            {!loading && result && <Results result={result} onReset={onReset} />}
          </main>
        </div>
      </div>
    </>
  );
}
